<script setup>
import { shared } from './shared.js'
</script>

<template>
    <div v-if="deviceDetail.aboard" class="card" style="margin-bottom: 20px;">
        <div class="card-header" style="background-color:#cfe2ff">
            <div class="form-check form-check-inline float-end"
                v-if="check_protocol()">
                <input class="form-check-input" type="checkbox" v-model="show_raw">
                <label class="form-label" style="margin-bottom: 0px;">Show raw</label>
            </div>
            <h5 style="margin-bottom: 0px;">Aboard</h5>
        </div>
        <div class="card-body" v-show="show_raw" style="padding: 0;">
            <Vue3JsonEditor class="json-raw-viewer" v-model="deviceDetail.aboard.data" :show-btns="false" :mode="'view'"
                :expandedOnStart="true" />
        </div>
        <div v-if="check_protocol()"
            class="card-body">
            <h5>System details</h5>
            <table class="table table-sm">
                <thead>
                    <tr>
                        <th width="150px"></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>State</td>
                        <td class="fw-bold">{{ get_descriptive("system.state") }}</td>
                    </tr>
                    <tr>
                        <td>Status</td>
                        <td>{{ get_descriptive("system.status") }}</td>
                    </tr>
                    <tr>
                        <td>Warning</td>
                        <td>{{ get_descriptive("system.warning") }}</td>
                    </tr>
                    <tr>
                        <td>Error</td>
                        <td>{{ get_descriptive("system.error") }}</td>
                    </tr>
                    <tr>
                        <td>Critical</td>
                        <td>{{ get_descriptive("system.critical") }}</td>
                    </tr>
                </tbody>
            </table>
            <div class="row">
                <div class="col-md-6">
                    <h5>Measurements</h5>
                    <table class="table table-sm text-end">
                        <thead>
                            <tr>
                                <th width="150px"></th>
                                <th width="100px"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="text-start">AC frequency</td>
                                <td>{{ get_data("avg.fgrid", 2) }} Hz
                                </td>
                            </tr>
                            <tr>
                                <td class="text-start">AC voltage</td>
                                <td>{{ get_data("avg.vgrid", 1) }} V</td>
                            </tr>
                            <tr>
                                <td class="text-start">AC current</td>
                                <td>{{ get_data("avg.igrid", 2)
                                    }} A
                                </td>
                            </tr>
                            <tr>
                                <td class="text-start">AC active power</td>
                                <td class="fw-bold">{{ get_data("avg.pgrid", 0)}} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">AC reactive power</td>
                                <td>{{ get_data("avg.qgrid", 0) }} var</td>
                            </tr>
                            <tr>
                                <td class="text-start">AC apparent power</td>
                                <td>{{ get_data("avg.sgrid", 0) }} VA</td>
                            </tr>
                        </tbody>
                    </table>
                    <h5>Temperatures</h5>
                    <table class="table table-sm text-end">
                        <thead>
                            <tr>
                                <th width="200px"></th>
                                <th width="100px"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="text-start fw-bold">A board</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td class="text-start">Board</td>
                                <td>{{ get_data("avg.tboard", 1) }} °C</td>
                            </tr>
                            <tr>
                                <td class="text-start">Module</td>
                                <td>{{ get_data("avg.tmod", 1) }} °C</td>
                            </tr>
                            <tr>
                                <td class="text-start">Heatsink</td>
                                <td>{{ get_data("avg.ths", 1) }} °C</td>
                            </tr>
                            <tr>
                                <td class="text-start fw-bold">B board</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td class="text-start">Board</td>
                                <td>{{ get_data("bb.tboard._value", 1) }} °C</td>
                            </tr>
                            <tr>
                                <td class="text-start">Step-up</td>
                                <td>{{ get_data("bb.tsu._value", 1) }} °C</td>
                            </tr>
                            <tr>
                                <td class="text-start">Step-down</td>
                                <td>{{ get_data("bb.tsd._value", 1) }} °C</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-md-6">
                    <h5>Limits</h5>
                    <table class="table table-sm text-end">
                        <thead>
                            <tr>
                                <th width="200px"></th>
                                <th width="100px"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="text-start fw-bold">Battery</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td class="text-start">Charge limit</td>
                                <td>{{ get_data("battery_limits_p.charge", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">Discharge limit</td>
                                <td>{{ get_data("battery_limits_p.discharge", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start fw-bold">A board</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td class="text-start">Board</td>
                                <td>{{ get_data("ab.tboard.pmax", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">Module</td>
                                <td>{{ get_data("ab.tmod.pmax", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">Heatsink</td>
                                <td>{{ get_data("ab.ths.pmax", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start fw-bold">B board</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td class="text-start">Board</td>
                                <td>{{ get_data("bb.tboard.pmax", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">Step-up</td>
                                <td>{{ get_data("bb.tsu.pmax", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">Step-down</td>
                                <td>{{ get_data("bb.tsd.pmax", 0) }} W</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ['deviceDetail'],
    setup() { },
    data() {
        return {
            show_raw: 0,
        };
    },
    methods: {
        check_protocol() {
            return this.deviceDetail.info && this.deviceDetail.info.protocol_version != null
        },
        get_data(parameter, round = null) {
            return shared.get_nested_value_safely(this.deviceDetail.aboard.data, parameter, round)
        },
        get_descriptive(parameter) {
            return shared.get_descriptive_safely(this.deviceDetail.aboard.data, parameter)
        },
    },
    created() {
        if (this.deviceDetail.info.protocol_version == null) {
            this.show_raw = 1;
        }
    },
};
</script>